import React, { useState, useEffect } from "react"
import { Row, Col, Form } from "react-bootstrap"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { getAgentRefundSummary } from "../../../apis/agents"
import RefundAttributeTextInput from "./RefundAttributeTextInput"
import RefundCalculationContainer from "./RefundCalculationContainer"
import RewardsRefundContainer from "./RewardsRefundContainer"
import { v4 as uuidv4 } from "uuid"
import findCaseAttribute from "../helpers/findCaseAttribute"
import { formatCurrency } from "@utils/formatters"
import { EditCasesAddress } from "@components/CasesNew/EditCasesAddress/EditCasesAddress"

const caseTextAttributeOrder = [
  "refund_amount",
  "refund_letter_location",
  "recipient_first_name",
  "recipient_last_name",
]

const CaseRefundContainer = ({
  caseTypeAttributes,
  setCaseTypeAttributes,
  address,
  handleUpdateCaseAttribute,
  accountId,
  prospectGuid,
  caseCreatedByAgentId,
  caseCreatedDt,
}) => {
  const { data: userInfo } = useAuthUserDetails()
  const [agentRefundSummary, setAgentRefundSummary] = useState(null)
  const isRewardsRefund = caseTypeAttributes.some(
    (attribute) => attribute.data_name === "refund_reward_ids",
  )
  const caseAddressAttribute = findCaseAttribute(
    "refund_address_guid",
    caseTypeAttributes,
  )
  const caseRefundCalculationAttribute = findCaseAttribute(
    "refund_calculation",
    caseTypeAttributes,
  )

  const handleAddressUpdate = (address_guid) => {
    if (address_guid && caseAddressAttribute) {
      handleUpdateCaseAttribute({
        case_attribute_id: caseAddressAttribute.case_attribute_id,
        value: address_guid,
      })
    }
  }

  useEffect(() => {
    const requestedDt = caseCreatedDt ? new Date(caseCreatedDt) : new Date()
    const month = requestedDt.getUTCMonth() + 1 // months are 0-based
    const year = requestedDt.getUTCFullYear()

    const params = {
      user_id: caseCreatedByAgentId || userInfo?.user_id,
      month,
      year,
    }

    getAgentRefundSummary(params, setAgentRefundSummary)
  }, [caseTypeAttributes, caseCreatedDt, userInfo, caseCreatedByAgentId])

  const attributeTextInputs = () => {
    return caseTypeAttributes
      .filter((attribute) => attribute.data_type === "text")
      .sort((a, b) => {
        const getOrder = (value) => {
          const index = caseTextAttributeOrder.indexOf(value)
          return index === -1 ? Number.MAX_SAFE_INTEGER : index
        }
        return getOrder(a.data_name) - getOrder(b.data_name)
      })
      .map((attributeInput) => {
        return (
          <RefundAttributeTextInput
            key={uuidv4()}
            attributeInput={attributeInput}
            caseTypeAttributes={caseTypeAttributes}
            setCaseTypeAttributes={setCaseTypeAttributes}
            handleUpdateCaseAttribute={handleUpdateCaseAttribute}
          />
        )
      })
  }

  return (
    <>
      {agentRefundSummary && (
        <>
          <Row className="mt-3">
            <Form.Group controlId="agent-refund-summary">
              <Form.Label>
                <b>Agent Refund Summary</b>
              </Form.Label>
            </Form.Group>
          </Row>

          <Row className="border">
            <Row className="mt-1">
              <Col md={2}>
                <b>Spent: </b>
              </Col>
              <Col md={2}>
                <b>Remaining: </b>
              </Col>
            </Row>
            <Row className="mt-1">
              <Col md={2}>
                <p>{formatCurrency(Number(agentRefundSummary.spent))}</p>
              </Col>
              <Col md={2}>
                <p>
                  {formatCurrency(
                    agentRefundSummary.limit - Number(agentRefundSummary.spent),
                  )}
                </p>
              </Col>
            </Row>
          </Row>
        </>
      )}
      {address && (
        <>
          <Row className="mt-3">
            <b>Address</b>
          </Row>
          {handleUpdateCaseAttribute ? (
            <Row className="mt-3">
              <EditCasesAddress
                refund_address_guid={caseAddressAttribute.value}
                prospect_guid={prospectGuid}
                handleUpdateCaseAttribute={handleAddressUpdate}
              />
            </Row>
          ) : (
            <Row className="mt-1">
              <p className="mb-0">
                {[address.street, address.city, address.state_code, address.zip]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              <Form.Text muted>
                Address can be updated after the case is created.
              </Form.Text>
            </Row>
          )}
          {isRewardsRefund && (
            <RewardsRefundContainer
              caseTypeAttributes={caseTypeAttributes}
              setCaseTypeAttributes={setCaseTypeAttributes}
              accountId={accountId}
              handleUpdateCaseAttribute={handleUpdateCaseAttribute}
            />
          )}
          {caseRefundCalculationAttribute && (
            <RefundCalculationContainer
              caseTypeAttributes={caseTypeAttributes}
              setCaseTypeAttributes={setCaseTypeAttributes}
              handleUpdateCaseAttribute={handleUpdateCaseAttribute}
            />
          )}
          {attributeTextInputs()}
        </>
      )}
    </>
  )
}

export default CaseRefundContainer
